// Decision Room detail view — V1 layout shell (2026-04-26).
//
// Renders the seller's editing surface for one Decision Room with the
// 9-section sidebar locked in project_buyer_spaces.md:
//   1. Welcome
//   2. Why We're Here (Discovery)
//   3. Demos
//   4. Documents
//   5. Mutual Action Plan
//   6. People (two-sided ledger)
//   7. Conversations
//   8. ROI
//   9. Customer Stories
//
// Each section has a per-Space visibility toggle (eye icon) that the
// seller flips to hide the section from the prospect's view of this
// Space. The toggle PATCHes /api/buyer-spaces/:id with
// sectionVisibility: { [section]: bool }.
//
// V1 ships the LAYOUT SHELL — sidebar nav, section routing, header,
// visibility toggles, and a placeholder per section. Real CRUD UI
// for each section lands in the next pass; the data is already
// available from GET /api/buyer-spaces/:id.

import { useEffect, useState, useMemo } from 'react';
import {
  WelcomeEditor,
  DiscoveryEditor,
  MapEditor,
  PeopleEditor,
  RoiEditor,
  DocumentsEditor,
  ConversationsEditor,
  EngagementPanel,
  ReviseWithAI,
} from './BuyerSpaceEditors';
import ReelThumb from './ReelThumb';
import { splitRecordings, isFullRecording, kindLabel, kindBlurb, countsSummary } from '../utils/roomRecordings';
import CoveragePanel from './CoveragePanel';
import InternalSharePanel from './InternalSharePanel';
import RoomFormsSection from './RoomFormsSection';
import ProposalEditor from './ProposalEditor';
import RoomCrmPanel from './RoomCrmPanel';
import RoomStatusControl from './RoomStatusControl';

// Match the API_BASE fallback used in App.jsx — without it, fetch
// hits the SPA's own Vercel domain and returns index.html, blowing
// up the JSON parse with "Unexpected token '<'".
const API_BASE = process.env.REACT_APP_API_URL || 'https://trailercst-backend-production.up.railway.app';

// Section ids drive both the sidebar label AND the visibility map
// keys, so they have to match the backend section_visibility JSONB
// defaults (project_buyer_spaces.md).
//
// defaultVisible mirrors the DB defaults so the eye-toggle reads
// correctly on a freshly-created Space whose visibility map is
// missing entries (older Spaces, schema-default rows, etc.).
//
// sellerOnly: true marks sections that NEVER show on the prospect
// view regardless of any visibility toggle. The eye-toggle isn't
// rendered for these (visibility map doesn't apply). Engagement
// analytics are the canonical example — surfacing them to the
// prospect would expose internal sales intelligence.
const SECTIONS = [
  { id: 'engagement',       label: 'Engagement',         sellerOnly: true       },
  { id: 'welcome',          label: 'Welcome',            defaultVisible: true   },
  { id: 'discovery',        label: "Why We're Here",     defaultVisible: true   },
  { id: 'demos',            label: 'Demos',              defaultVisible: true   },
  { id: 'documents',        label: 'Documents',          defaultVisible: true   },
  { id: 'map',              label: 'Mutual Action Plan', defaultVisible: true   },
  { id: 'people',           label: 'People',             defaultVisible: true   },
  { id: 'conversations',    label: 'Conversations',      defaultVisible: true   },
  { id: 'roi',              label: 'ROI',                defaultVisible: false  },
  { id: 'customer_stories', label: 'Customer Stories',   defaultVisible: false  },
  // Expansion pack (2026-09-07). Each is dark behind its release flag —
  // requiresFlag hides the row entirely until the flag is on for the
  // workspace, so a room never shows a section its backend would 404.
  // Forms and Proposal are buyer-facing sections (hidden by default —
  // assigning a form / sharing a proposal flips them on server-side);
  // CRM deal is seller-only, like Engagement.
  { id: 'forms',            label: 'Forms',              defaultVisible: false, requiresFlag: 'forms'     },
  { id: 'proposal',         label: 'Proposal',           defaultVisible: false, requiresFlag: 'proposals' },
  // Handoff Brief is the post-close artifact for the implementation
  // team. Internal-only by definition (the brief itself contains
  // internal notes + transcripts), so sellerOnly bypasses the
  // visibility toggle the same way Engagement does.
  { id: 'handoff',          label: 'Handoff Brief',      sellerOnly: true       },
  { id: 'crm',              label: 'CRM deal',           sellerOnly: true, requiresFlag: 'crm_depth' },
];

function fmtDate(s) {
  if (!s) return '';
  try { return new Date(s).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return s; }
}

// EyeIcon reflects the section's prospect-visibility flag. Click to
// toggle. Filled-eye = visible, slashed-eye = hidden.
function EyeToggle({ visible, onClick, title }) {
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      title={title}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: visible ? '#a855f7' : 'var(--room-text-subtle, #94a3b8)', padding: 4,
        display: 'inline-flex', alignItems: 'center',
      }}
    >
      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        {visible ? (
          <>
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </>
        ) : (
          <>
            <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94" />
            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </>
        )}
      </svg>
    </button>
  );
}

// Parse the section subpath from the URL hash for refresh-resume.
// Hash shape is #/buyer-spaces/<spaceId>/<sectionId>[/<entityId>]
// where the optional 4th segment is a section-specific deep-link
// target (e.g. a MAP action ID for "Open conversation" from the
// inbox). We own segments 3+ here; BuyerSpacesPhase owns segment 2
// (the spaceId).
function sectionFromHash(validIds) {
  if (typeof window === 'undefined') return null;
  const raw = (window.location.hash || '').replace(/^#\/?/, '');
  const parts = raw.split('/');
  if (parts[0] !== 'buyer-spaces') return null;
  const candidate = parts[2];
  return candidate && validIds.has(candidate) ? candidate : null;
}

function entityFromHash() {
  if (typeof window === 'undefined') return null;
  const raw = (window.location.hash || '').replace(/^#\/?/, '');
  const parts = raw.split('/');
  if (parts[0] !== 'buyer-spaces') return null;
  return parts[3] || null;
}

export default function BuyerSpaceDetail({ authToken, spaceId, onBack, onOpenDemo, sellerName, currentUserId, isCompanyAdmin, uiFlags }) {
  // Internal share moves out of the buyer-facing button stack and down
  // into the internal group (Collaborators) when this is on.
  const internalPlacementMoved = uiFlags?.rooms_internal_share_placement === true;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState(() => {
    const validIds = new Set(SECTIONS.map((s) => s.id));
    return sectionFromHash(validIds) || 'welcome';
  });
  // 4th hash segment — section-specific deep-link target. Read once
  // on mount; cleared as soon as the seller switches sections (or
  // performs a UI action that consumes the deep-link, like expanding
  // the action). MapEditor reads this to auto-open the right comment
  // thread when arriving from the Conversations inbox.
  const [pendingEntityId, setPendingEntityId] = useState(() => entityFromHash());
  const [savingVisibility, setSavingVisibility] = useState(false);

  // Mirror activeSection ↔ URL hash so a refresh inside a Space
  // returns the seller to the same section instead of dropping them
  // back at "Welcome". BuyerSpacesPhase owns the spaceId portion of
  // the path; we just append /<sectionId>. We preserve any 4th
  // segment (entity ID) only on the initial mount — once the seller
  // navigates between sections via the sidebar, the deep-link is
  // considered consumed and we drop it from the URL.
  useEffect(() => {
    if (typeof window === 'undefined' || !spaceId) return;
    const tail = pendingEntityId ? `/${pendingEntityId}` : '';
    const target = `#/buyer-spaces/${spaceId}/${activeSection}${tail}`;
    if (window.location.hash !== target) {
      window.history.replaceState(null, '', window.location.pathname + window.location.search + target);
    }
  }, [spaceId, activeSection, pendingEntityId]);
  // Inline rename of the Space's prospectCompanyName. The seller
  // clicks the sidebar header h2 → it swaps to a text input that
  // saves on blur or Enter.
  const [renaming, setRenaming] = useState(false);
  const [renameDraft, setRenameDraft] = useState('');

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body.error || `Failed to load (status ${r.status})`);
      }
      const json = await r.json();
      setData(json);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [spaceId]);

  const visibility = useMemo(() => {
    if (!data) return {};
    return data.buyerSpace?.sectionVisibility || {};
  }, [data]);

  const toggleVisibility = async (sectionId) => {
    if (!data) return;
    // Resolve the current visible-state with the same precedence the
    // sidebar render uses (explicit boolean wins, else defaultVisible).
    const sectionDef = SECTIONS.find((s) => s.id === sectionId);
    const current = (() => {
      const stored = data.buyerSpace.sectionVisibility?.[sectionId];
      if (typeof stored === 'boolean') return stored;
      return sectionDef ? sectionDef.defaultVisible : true;
    })();
    const next = !current;

    setSavingVisibility(true);
    // Optimistic update — read from the latest data via the setter
    // callback so we don't write a stale snapshot back.
    setData((prev) => ({
      ...prev,
      buyerSpace: {
        ...prev.buyerSpace,
        sectionVisibility: { ...(prev.buyerSpace.sectionVisibility || {}), [sectionId]: next },
      },
    }));

    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ sectionVisibility: { [sectionId]: next } }),
      });
      if (!r.ok) throw new Error(`Visibility save failed (status ${r.status})`);
      // Trust the server's merged map on success.
      const body = await r.json().catch(() => null);
      if (body && body.buyerSpace) {
        setData((prev) => ({ ...prev, buyerSpace: body.buyerSpace }));
      }
    } catch (err) {
      console.error('[buyer-spaces] toggle visibility failed:', err.message);
      // Revert on failure so the UI reflects what's actually persisted.
      setData((prev) => ({
        ...prev,
        buyerSpace: {
          ...prev.buyerSpace,
          sectionVisibility: { ...(prev.buyerSpace.sectionVisibility || {}), [sectionId]: current },
        },
      }));
    } finally {
      setSavingVisibility(false);
    }
  };

  // Rename handler — fires on blur/Enter. Empty / unchanged values
  // are no-ops so an accidental click+blur doesn't blank the name.
  const commitRename = async () => {
    const next = renameDraft.trim();
    setRenaming(false);
    if (!next || !data || next === data.buyerSpace.prospectCompanyName) return;
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ prospectCompanyName: next }),
      });
      if (!r.ok) throw new Error(`Rename failed (${r.status})`);
      const body = await r.json();
      if (body.buyerSpace) setData((prev) => ({ ...prev, buyerSpace: body.buyerSpace }));
    } catch (err) {
      console.error('[buyer-spaces] rename failed:', err.message);
    }
  };

  // Only show the full-screen loader on the FIRST load (when data
  // is still null). Subsequent reloads — triggered after a doc
  // toggle, MAP checkmark, or other in-place mutation — keep the
  // existing UI mounted while the fetch happens in the background.
  // Without this gate every onReload() blanked the editor for ~300ms
  // and read as a screen flash.
  if (loading && !data) {
    return (
      <div style={{ padding: 40, textAlign: 'center', color: 'var(--room-text-muted)' }}>
        Loading Decision Room…
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: 40, maxWidth: 600, margin: '0 auto' }}>
        <button type="button" onClick={onBack} style={{ marginBottom: 16, padding: '8px 14px', borderRadius: 8, border: '1px solid var(--room-border)', background: 'var(--room-card)', cursor: 'pointer' }}>
          ← Back to Decision Rooms
        </button>
        <div style={{ padding: 16, borderRadius: 12, background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)', color: '#dc2626' }}>
          <strong>Couldn't load:</strong> {error}
        </div>
      </div>
    );
  }
  if (!data) return null;

  const space = data.buyerSpace;
  // Flag-gated sections drop out of the sidebar until their flag is on
  // for this workspace (uiFlags comes from /auth/me via BuyerSpacesPhase).
  const visibleSections = SECTIONS.filter((s) => !s.requiresFlag || (uiFlags && uiFlags[s.requiresFlag] === true));

  return (
    // The Decision Room editor honors the global theme. Surface
    // colors come from --room-* CSS variables defined in App.css
    // (see :root + [data-theme="dark"] blocks). Light mode keeps
    // the existing slate / white palette; dark mode flips to a
    // dashboard-matching dark surface.
    //
    // The prospect-facing view (BuyerSpaceProspectView) is still
    // intentionally locked to light — that's an external, branded
    // experience that needs to look the same regardless of which
    // theme the seller has on for their internal dashboard. So this
    // editor adapts; the prospect preview doesn't.
    <div style={{
      display: 'flex',
      minHeight: 'calc(100vh - 64px)',
      background: 'var(--room-surface)',
      color: 'var(--room-text-strong)',
    }}>
      {/* ─── Left section sidebar ────────────────────────────────── */}
      <aside style={{
        width: 260, background: 'var(--room-card)', borderRight: '1px solid var(--room-border)',
        padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: 4,
      }}>
        <button
          type="button"
          onClick={onBack}
          style={{
            background: 'none', border: 'none', color: 'var(--room-text-muted)',
            cursor: 'pointer', padding: '8px 12px', textAlign: 'left',
            fontSize: 13, marginBottom: 8,
          }}
        >
          ← All Decision Rooms
        </button>
        <div style={{ padding: '0 12px 16px', borderBottom: '1px solid var(--room-border-soft)', marginBottom: 12 }}>
          {renaming ? (
            <input
              type="text"
              value={renameDraft}
              onChange={(e) => setRenameDraft(e.target.value)}
              onBlur={commitRename}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitRename();
                if (e.key === 'Escape') { setRenaming(false); setRenameDraft(''); }
              }}
              autoFocus
              style={{
                width: '100%', fontSize: 16, fontWeight: 700, color: 'var(--room-text-strong)',
                padding: '4px 6px', border: '1px solid #c4b5fd', borderRadius: 6,
                fontFamily: 'inherit', background: 'var(--room-accent-soft, #faf5ff)', boxSizing: 'border-box',
              }}
            />
          ) : (
            <h2
              onClick={() => { setRenameDraft(space.prospectCompanyName); setRenaming(true); }}
              title="Click to rename"
              style={{
                margin: 0, fontSize: 16, fontWeight: 700, color: 'var(--room-text-strong)',
                cursor: 'pointer', padding: '2px 4px', borderRadius: 4,
                transition: 'background 0.12s ease',
              }}
              onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--room-card-hover)'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
            >
              {space.prospectCompanyName}
            </h2>
          )}
          <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--room-text-subtle)' }}>
            /space/{space.slug}
          </p>
          {/* Share menu — primary action for getting the Decision Room
              URL out to a prospect. Each row in the modal offers
              Copy / Email / Preview scoped to one stakeholder. */}
          <ShareSpaceMenu
            slug={space.slug}
            spaceId={spaceId}
            authToken={authToken}
            people={data.people || []}
            prospectCompanyName={space.prospectCompanyName}
            sellerName={sellerName || ''}
            copyLinkEnabled={uiFlags?.rooms_copy_share_link === true}
            internalPlacementMoved={internalPlacementMoved}
          />

          {/* Preview-as-prospect — works regardless of whether
              stakeholders have been added (mints a seller-owned
              preview token if no distributions exist yet, so the
              seller can see what the Space looks like before they
              start filling the People tab). Decoupled from the Share
              flow so "let me check what this looks like" is one
              click instead of "open Share modal → pick a person →
              click Preview". */}
          <PreviewAsProspectButton
            slug={space.slug}
            spaceId={spaceId}
            authToken={authToken}
          />

          {/* Collaborators — owner can invite same-company teammates
              to contribute (V2 of topic 1, 2026-04-27 security
              review). Adders/removers gated on owner-or-admin; the
              row renders for everyone (owners, admins, collaborators)
              so anyone with access can see who else is on the deal. */}
          <CollaboratorsSection
            spaceId={spaceId}
            authToken={authToken}
            // Owner OR company admin can manage collaborators.
            // Collaborators themselves see the read-only chip list
            // but no Add button + no remove ×. (The backend
            // requireOwnerOrAdmin gate enforces this regardless of
            // what the FE shows, but hiding the affordances avoids
            // the dead-click annoyance.)
            canManage={
              !!isCompanyAdmin
              || (space.ownerUserId != null && space.ownerUserId === currentUserId)
            }
          />

          {/* Internal share, grouped with the other internal-audience
              control (2026-09-07, flag rooms_internal_share_placement).
              Sibling of CollaboratorsSection on purpose — that section
              hides itself in several states and must not take this
              with it. Available even with no buyer-side people yet,
              which the old placement inside ShareSpaceMenu's
              eligible-people branch was not. */}
          {internalPlacementMoved && (
            <InternalShareButton spaceId={spaceId} authToken={authToken} variant="grouped" />
          )}

          {/* Outcome — Open / Won / Lost, with the loss-reason picker
              when the loss_reasons flag is on (2026-09-07). */}
          <RoomStatusControl
            spaceId={spaceId}
            authToken={authToken}
            currentStatus={space.status}
            uiFlags={uiFlags}
            onChanged={() => load()}
          />

          {/* Archive + Delete — destructive actions in a tight row
              below the primaries. Confirmations protect against
              accidental clicks; Delete is hard-delete (cascades
              MAP / docs / comments / ROI / people). Trailers + share
              links survive (FK ON DELETE SET NULL). */}
          <SpaceLifecycleActions
            spaceId={spaceId}
            authToken={authToken}
            currentStatus={space.status}
            onArchived={() => load()}
            onDeleted={() => onBack()}
            // Transfer ownership is admin-only and only meaningful
            // when the admin is viewing a Space they don't own.
            // Detect via currentUserId vs space.ownerUserId.
            canTransfer={!!isCompanyAdmin && currentUserId != null && space.ownerUserId != null && space.ownerUserId !== currentUserId}
            onTransferred={() => load()}
          />
        </div>

        {visibleSections.map((s) => {
          const isActive = activeSection === s.id;
          // Seller-only sections (Engagement) skip the eye-toggle —
          // they're never prospect-visible regardless of map state.
          if (s.sellerOnly) {
            // Whole row is one button — it contains no other
            // interactive control, unlike the regular entries below.
            // (2026-08-27 a11y fix: these were clickable divs with no
            // role, invisible to keyboards and screen readers.)
            return (
              <button
                type="button"
                key={s.id}
                aria-current={isActive ? 'true' : undefined}
                onClick={() => setActiveSection(s.id)}
                style={{
                  width: '100%', border: 0, font: 'inherit', textAlign: 'left',
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                  background: isActive ? 'rgba(168, 85, 247, 0.08)' : 'transparent',
                  color: isActive ? '#7c3aed' : 'var(--room-text-body, #334155)',
                  fontWeight: isActive ? 600 : 500, fontSize: 14,
                }}
              >
                <span style={{ flex: 1 }}>{s.label}</span>
                <span
                  title="Seller-only — never visible to prospects"
                  style={{ fontSize: 10, color: 'var(--room-text-subtle)', textTransform: 'uppercase', letterSpacing: '0.05em' }}
                >
                  Internal
                </span>
              </button>
            );
          }
          // Visibility resolution:
          //   - explicit boolean in the map wins (true OR false — both
          //     are valid stored values once the seller has toggled)
          //   - missing key → use the section's defaultVisible
          // The earlier version conflated `false` with "missing", which
          // made toggling-off non-default sections silently snap back.
          const stored = visibility[s.id];
          const isVisibleToProspect = typeof stored === 'boolean' ? stored : s.defaultVisible;
          return (
            <div
              key={s.id}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '8px 12px', borderRadius: 8,
                background: isActive ? 'rgba(168, 85, 247, 0.08)' : 'transparent',
                color: isActive ? '#7c3aed' : 'var(--room-text-body, #334155)',
                fontWeight: isActive ? 600 : 500, fontSize: 14,
              }}
            >
              {/* Navigation lives on the LABEL button; the eye toggle is
                  a sibling, never a nested control (2026-08-27 a11y fix:
                  these rows were clickable divs with no role at all). */}
              <button
                type="button"
                aria-current={isActive ? 'true' : undefined}
                onClick={() => setActiveSection(s.id)}
                style={{
                  flex: 1, textAlign: 'left', background: 'none', border: 0,
                  font: 'inherit', color: 'inherit', cursor: 'pointer', padding: 0,
                }}
              >
                {s.label}
              </button>
              <EyeToggle
                visible={isVisibleToProspect}
                onClick={() => toggleVisibility(s.id)}
                title={isVisibleToProspect
                  ? `Hide "${s.label}" from prospect`
                  : `Show "${s.label}" to prospect`}
              />
            </div>
          );
        })}

        <div style={{ marginTop: 'auto', paddingTop: 16, borderTop: '1px solid var(--room-border-soft)', fontSize: 11, color: 'var(--room-text-subtle)' }}>
          {savingVisibility ? 'Saving…' : 'Eye icon = visible to prospect'}
        </div>
      </aside>

      {/* ─── Main panel ──────────────────────────────────────────── */}
      <main style={{ flex: 1, padding: '32px 40px', overflow: 'auto' }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, marginBottom: 24 }}>
            <h1 style={{ margin: 0, fontSize: 28, fontWeight: 700 }}>
              {SECTIONS.find((s) => s.id === activeSection)?.label}
            </h1>
            <span style={{ fontSize: 13, color: 'var(--room-text-subtle)' }}>
              · {space.prospectCompanyName}
            </span>
          </div>

          {/* Each section renders its own placeholder. Real CRUD UI
              lands in the next pass; data is already in `data` from
              the API call above. */}
          <SectionContent
            section={activeSection}
            data={data}
            authToken={authToken}
            spaceId={spaceId}
            currentUserName={sellerName || ''}
            onReload={load}
            onOpenDemo={onOpenDemo}
            pendingEntityId={pendingEntityId}
            consumeEntityId={() => setPendingEntityId(null)}
            uiFlags={uiFlags}
          />
        </div>
      </main>
    </div>
  );
}

// ─── Section content router ───────────────────────────────────────
// Each branch renders a placeholder + the data we have. Designed
// so the next session can replace each placeholder with full CRUD
// UI without restructuring the parent.

function SectionContent({ section, data, authToken, spaceId, currentUserName, onReload, onOpenDemo, pendingEntityId, consumeEntityId, uiFlags }) {
  const space = data.buyerSpace;

  if (section === 'engagement') {
    return <EngagementPanel data={data} authToken={authToken} spaceId={spaceId} />;
  }

  if (section === 'welcome') {
    return <WelcomeEditor data={data} authToken={authToken} spaceId={spaceId} onReload={onReload} />;
  }

  if (section === 'discovery') {
    return <DiscoveryEditor data={data} authToken={authToken} spaceId={spaceId} onReload={onReload} />;
  }

  if (section === 'demos') {
    return (
      <DemosSection
        data={data}
        authToken={authToken}
        spaceId={spaceId}
        onReload={onReload}
        onOpenDemo={onOpenDemo}
      />
    );
  }

  if (section === 'documents') {
    return (
      <DocumentsEditor
        data={data}
        authToken={authToken}
        spaceId={spaceId}
        currentUserName={currentUserName}
        onReload={onReload}
      />
    );
  }

  if (section === 'map') {
    return (
      <MapEditor
        data={data}
        authToken={authToken}
        spaceId={spaceId}
        onReload={onReload}
        // pendingEntityId carries the deep-link MAP action ID from the
        // Conversations inbox. MapEditor auto-expands that action's
        // comment thread + scrolls to it, then calls consumeEntityId
        // so a subsequent re-render doesn't fight the user manually
        // collapsing it.
        autoExpandActionId={pendingEntityId || null}
        onAutoExpandConsumed={consumeEntityId}
      />
    );
  }

  if (section === 'people') {
    // Coverage panel above the editor (2026-08-05 ROI pack): the same
    // committee the seller curates here, scored by actual engagement —
    // engaged / gone quiet / never engaged — so single-threading is
    // visible in the exact place stakeholders get managed.
    return (
      <>
        <CoveragePanel authToken={authToken} spaceId={spaceId} />
        <PeopleEditor data={data} authToken={authToken} spaceId={spaceId} onReload={onReload} uiFlags={uiFlags} />
      </>
    );
  }

  if (section === 'conversations') {
    return <ConversationsEditor data={data} authToken={authToken} spaceId={spaceId} uiFlags={uiFlags} />;
  }

  if (section === 'roi') {
    return <RoiEditor data={data} authToken={authToken} spaceId={spaceId} onReload={onReload} />;
  }

  if (section === 'customer_stories') {
    return (
      <DocumentsEditor
        data={data}
        authToken={authToken}
        spaceId={spaceId}
        currentUserName={currentUserName}
        onReload={onReload}
        categoryFilter="customer_story"
      />
    );
  }

  if (section === 'forms') {
    return <RoomFormsSection authToken={authToken} spaceId={spaceId} onReload={onReload} />;
  }

  if (section === 'proposal') {
    return <ProposalEditor data={data} authToken={authToken} spaceId={spaceId} onReload={onReload} />;
  }

  if (section === 'crm') {
    return <RoomCrmPanel authToken={authToken} spaceId={spaceId} onPeopleChanged={onReload} />;
  }

  if (section === 'handoff') {
    return <HandoffBriefPanel data={data} authToken={authToken} spaceId={spaceId} />;
  }

  return null;
}

// Preview-as-prospect button. Posts to /preview-token which either
// returns an existing distribution's token or mints a fresh
// seller-owned one if the Space has no recipients yet. Either way
// the seller gets to see the prospect view in a new tab without
// touching the Share modal.
function PreviewAsProspectButton({ slug, spaceId, authToken }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const open = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/preview-token`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Preview failed (${r.status})`);
      }
      const body = await r.json();
      const url = `${window.location.origin}/space/${encodeURIComponent(body.slug)}?t=${encodeURIComponent(body.token)}`;
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ marginTop: 8 }}>
      <button
        type="button"
        onClick={open}
        disabled={busy}
        style={{
          width: '100%', padding: '8px 14px', borderRadius: 10,
          border: '1px solid #c4b5fd',
          background: 'var(--room-accent-soft, #faf5ff)', color: '#7c3aed',
          cursor: busy ? 'wait' : 'pointer',
          fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
          transition: 'background 0.12s ease, color 0.12s ease',
          opacity: busy ? 0.6 : 1,
        }}
        onMouseEnter={(e) => { if (!busy) { e.currentTarget.style.background = '#7c3aed'; e.currentTarget.style.color = '#fff'; } }}
        onMouseLeave={(e) => { if (!busy) { e.currentTarget.style.background = 'var(--room-accent-soft, #faf5ff)'; e.currentTarget.style.color = '#7c3aed'; } }}
      >
        {busy ? 'Opening…' : 'Preview as prospect ↗'}
      </button>
      {error && (
        <p style={{ fontSize: 12, color: '#dc2626', margin: '6px 0 0' }}>
          {error}
        </p>
      )}
    </div>
  );
}

// Collaborators section — V2 of topic 1 in the 2026-04-27 security
// review. Renders right under the Share + Preview buttons in the
// sidebar so collaboration is a discoverable positive action. Owner
// + admins see the Add button + per-chip remove ×; everyone with
// access sees the read-only chip list.
function CollaboratorsSection({ spaceId, authToken, canManage }) {
  const [collaborators, setCollaborators] = useState(null);
  const [error, setError] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [removingUserId, setRemovingUserId] = useState(null);

  const load = async () => {
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/collaborators`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Failed to load (${r.status})`);
      }
      const body = await r.json();
      setCollaborators(Array.isArray(body.collaborators) ? body.collaborators : []);
    } catch (err) {
      setError(err.message);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [spaceId]);

  const remove = async (userId, displayName) => {
    if (!window.confirm(
      `Remove ${displayName} as a collaborator?\n\n` +
      `They'll lose edit access to this Decision Room immediately. ` +
      `You can re-add them later if needed.`
    )) return;
    setRemovingUserId(userId);
    try {
      const r = await fetch(
        `${API_BASE}/api/buyer-spaces/${spaceId}/collaborators/${userId}`,
        { method: 'DELETE', headers: { Authorization: `Bearer ${authToken}` } }
      );
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Remove failed (${r.status})`);
      }
      load();
    } catch (err) {
      alert(err.message);
    } finally {
      setRemovingUserId(null);
    }
  };

  if (error) {
    return (
      <div style={{ marginTop: 12, fontSize: 11, color: '#dc2626' }}>
        Couldn't load collaborators: {error}
      </div>
    );
  }
  if (collaborators === null) {
    return (
      <div style={{ marginTop: 12, fontSize: 11, color: 'var(--room-text-subtle)' }}>Loading collaborators…</div>
    );
  }

  // When there are no collaborators AND the caller can't manage, the
  // section adds visual noise without information. Hide it.
  if (collaborators.length === 0 && !canManage) return null;

  return (
    <div style={{ marginTop: 14 }}>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        marginBottom: 6,
      }}>
        <div style={{
          fontSize: 11, fontWeight: 700,
          textTransform: 'uppercase', letterSpacing: '0.06em',
          color: 'var(--room-text-muted)',
        }}>
          Collaborators
        </div>
        {canManage && (
          <button
            type="button"
            onClick={() => setAddOpen(true)}
            title="Invite a teammate to contribute"
            style={{
              background: 'none', border: 'none', padding: '2px 6px',
              fontSize: 11, fontWeight: 600, color: '#7c3aed',
              cursor: 'pointer', fontFamily: 'inherit',
            }}
          >
            + Add
          </button>
        )}
      </div>
      {collaborators.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--room-text-subtle)' }}>
          No collaborators yet. Invite a teammate to contribute.
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {collaborators.map((c) => {
            const name = [c.firstName, c.lastName].filter(Boolean).join(' ')
              || c.username || c.email;
            return (
              <span
                key={c.userId}
                title={c.email}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  padding: '3px 8px', borderRadius: 100,
                  fontSize: 11, fontWeight: 600,
                  background: 'var(--room-card-hover)', color: 'var(--room-text-body)',
                  border: '1px solid var(--room-border)',
                }}
              >
                {name}
                {canManage && (
                  <button
                    type="button"
                    onClick={() => remove(c.userId, name)}
                    disabled={removingUserId === c.userId}
                    aria-label={`Remove ${name}`}
                    style={{
                      background: 'none', border: 'none', padding: '0 2px',
                      cursor: removingUserId === c.userId ? 'wait' : 'pointer',
                      color: 'var(--room-text-subtle)', fontSize: 13, lineHeight: 1,
                      fontFamily: 'inherit',
                    }}
                    onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.color = 'var(--room-text-subtle, #94a3b8)'; }}
                  >
                    ×
                  </button>
                )}
              </span>
            );
          })}
        </div>
      )}
      {addOpen && (
        <AddCollaboratorModal
          spaceId={spaceId}
          authToken={authToken}
          existingUserIds={new Set(collaborators.map((c) => c.userId))}
          onClose={() => setAddOpen(false)}
          onAdded={() => { setAddOpen(false); load(); }}
        />
      )}
    </div>
  );
}

// Modal for picking a teammate to invite. Mirrors the
// TransferOwnershipModal pattern — same /api/company/members fetch,
// same picker UX. Filters out the current owner + already-added
// collaborators so the list reflects who's actually addable.
function AddCollaboratorModal({ spaceId, authToken, existingUserIds, onClose, onAdded }) {
  const [members, setMembers] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickedId, setPickedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/company/members`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(async (r) => {
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.error || `Members load failed (${r.status})`);
        }
        return r.json();
      })
      .then((body) => {
        if (cancelled) return;
        const all = Array.isArray(body.members) ? body.members : [];
        // Drop the people who can't be added: existing collaborators
        // (already on the list) and self (the owner is implicit).
        // Owner-of-the-Space is also already implicit but might not
        // be the same as the API caller — backend rejects that case
        // explicitly so we leave them in the picker for clarity.
        const eligible = all.filter((m) => !existingUserIds.has(m.id));
        setMembers(eligible);
      })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [authToken, existingUserIds]);

  const submit = async () => {
    if (!pickedId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/collaborators`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: pickedId }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Add failed (${r.status})`);
      }
      onAdded();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="add-collab-modal-title"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--room-card)', borderRadius: 14, width: '100%', maxWidth: 480,
          maxHeight: '85vh', overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(15, 23, 42, 0.22)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid var(--room-border-soft)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h3 id="add-collab-modal-title" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--room-text-strong)' }}>
            Add a collaborator
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 22, color: 'var(--room-text-subtle)', padding: 4, lineHeight: 1,
            }}
          >×</button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--room-text-muted)', lineHeight: 1.5 }}>
            Invite a teammate to contribute to this Decision Room. They'll get the
            same edit access as you, except they can't delete the Space, transfer
            ownership, or manage collaborators. They'll get an email and an in-app
            notification when you add them.
          </p>
          {!members && !error && (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--room-text-subtle)' }}>Loading team…</p>
          )}
          {error && (
            <div style={{
              padding: '10px 12px', borderRadius: 8,
              background: 'rgba(239, 68, 68, 0.08)', color: '#dc2626', fontSize: 13,
            }}>
              {error}
            </div>
          )}
          {members && members.length === 0 && (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--room-text-muted)' }}>
              No more teammates to add — everyone's either already a collaborator or the owner.
            </p>
          )}
          {members && members.length > 0 && (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {members.map((m) => {
                const name = [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email;
                const isPicked = pickedId === m.id;
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => setPickedId(m.id)}
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 10,
                        border: `1px solid ${isPicked ? '#a855f7' : 'var(--room-border, #e2e8f0)'}`,
                        background: isPicked ? 'var(--room-accent-soft, #faf5ff)' : 'var(--room-card, #fff)',
                        cursor: 'pointer', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 12,
                        fontFamily: 'inherit',
                      }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: '#ede9fe', color: '#6d28d9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, flex: 'none',
                      }}>
                        {(name || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)' }}>
                          {name}
                          {m.companyRole === 'admin' && (
                            <span style={{ marginLeft: 8, fontSize: 10, color: '#7c3aed', fontWeight: 700, letterSpacing: '0.04em' }}>ADMIN</span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--room-text-subtle)' }}>
                          {m.email}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div style={{
          padding: '14px 20px', borderTop: '1px solid var(--room-border-soft)',
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              padding: '8px 16px', borderRadius: 8,
              border: '1px solid var(--room-border)', background: 'var(--room-card)',
              color: 'var(--room-text-body)', cursor: 'pointer',
              fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!pickedId || busy}
            style={{
              padding: '8px 18px', borderRadius: 8, border: 'none',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
              color: '#fff', cursor: (!pickedId || busy) ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              opacity: (!pickedId || busy) ? 0.55 : 1,
            }}
          >
            {busy ? 'Adding…' : 'Add collaborator'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Lifecycle actions — Archive (sets status='dormant') + Delete
// (hard-delete with cascade). Two small ghost buttons in a row, each
// with a confirm dialog. Archive flips the status pill on the list
// view; Delete drops the Space row and unbinds attached trailers/dists.
function SpaceLifecycleActions({ spaceId, authToken, currentStatus, onArchived, onDeleted, canTransfer, onTransferred }) {
  const [busy, setBusy] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const isArchived = currentStatus === 'dormant';

  const archive = async () => {
    if (!window.confirm(isArchived
      ? 'Reactivate this Decision Room? It will move back to Open status.'
      : 'Archive this Decision Room? It will be hidden from your active list but remains accessible to anyone with a share link.')) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}`, {
        method: 'PATCH',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: isArchived ? 'open' : 'dormant' }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Archive failed (${r.status})`);
      }
      onArchived();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
    }
  };

  const del = async () => {
    if (!window.confirm(
      'Delete this Decision Room PERMANENTLY?\n\n' +
      'This drops the MAP, documents, comments, ROI, and People entries. ' +
      'Attached trailers and share links survive — they go back to "ungrouped".\n\n' +
      'This cannot be undone.'
    )) return;
    setBusy(true);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Delete failed (${r.status})`);
      }
      onDeleted();
    } catch (err) {
      alert(err.message);
      setBusy(false);
    }
  };

  // Tight, low-emphasis row of icon-led ghost actions. Sits below the
  // primary "Share" + "Preview as prospect" buttons and reads as a
  // secondary cluster — same visual register as the EyeToggle pattern
  // in the section list. Hover bumps the tone toward the action's
  // intent (lavender for Archive/Reactivate, red for Delete) without
  // ever shouting at the seller from baseline.
  return (
    <div style={{ marginTop: 10, display: 'flex', gap: 6 }}>
      <button
        type="button"
        onClick={archive}
        disabled={busy}
        title={isArchived ? 'Reactivate this Decision Room' : 'Archive this Decision Room (status → Dormant)'}
        style={lifecycleBtnStyle({ busy, tone: 'neutral' })}
        onMouseEnter={(e) => { if (!busy) lifecycleBtnHover(e.currentTarget, 'neutral', true); }}
        onMouseLeave={(e) => { if (!busy) lifecycleBtnHover(e.currentTarget, 'neutral', false); }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
          {isArchived ? (
            <>
              <path d="M3 12a9 9 0 1 0 9-9" />
              <polyline points="3 4 3 9 8 9" />
            </>
          ) : (
            <>
              <rect x="3" y="3" width="18" height="5" rx="1" />
              <path d="M5 8v11a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8" />
              <line x1="10" y1="13" x2="14" y2="13" />
            </>
          )}
        </svg>
        <span>{isArchived ? 'Reactivate' : 'Archive'}</span>
      </button>
      <button
        type="button"
        onClick={del}
        disabled={busy}
        title="Permanently delete this Decision Room"
        style={lifecycleBtnStyle({ busy, tone: 'danger' })}
        onMouseEnter={(e) => { if (!busy) lifecycleBtnHover(e.currentTarget, 'danger', true); }}
        onMouseLeave={(e) => { if (!busy) lifecycleBtnHover(e.currentTarget, 'danger', false); }}
      >
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6l-2 14a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2L5 6" />
          <path d="M10 11v6" />
          <path d="M14 11v6" />
          <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
        </svg>
        <span>Delete</span>
      </button>
      {/* Transfer ownership — admin-only, only shown when admin is
          viewing a Space they don't own. Opens a modal with a member
          picker; the actual reassignment hits the new transfer
          endpoint and audit-logs the change.  */}
      {canTransfer && (
        <button
          type="button"
          onClick={() => setTransferOpen(true)}
          disabled={busy}
          title="Reassign this Decision Room to a different rep in your company"
          style={lifecycleBtnStyle({ busy, tone: 'neutral' })}
          onMouseEnter={(e) => { if (!busy) lifecycleBtnHover(e.currentTarget, 'neutral', true); }}
          onMouseLeave={(e) => { if (!busy) lifecycleBtnHover(e.currentTarget, 'neutral', false); }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
            <polyline points="17 1 21 5 17 9" />
            <path d="M3 11V9a4 4 0 0 1 4-4h14" />
            <polyline points="7 23 3 19 7 15" />
            <path d="M21 13v2a4 4 0 0 1-4 4H3" />
          </svg>
          <span>Transfer</span>
        </button>
      )}
      {transferOpen && (
        <TransferOwnershipModal
          spaceId={spaceId}
          authToken={authToken}
          onClose={() => setTransferOpen(false)}
          onTransferred={() => {
            setTransferOpen(false);
            if (onTransferred) onTransferred();
          }}
        />
      )}
    </div>
  );
}

// Modal for picking a new owner. Loads the company's member list,
// filters to non-current-owner active users, lets the admin click
// one to reassign. Single POST + close on success. Out-of-scope to
// ship: confirmation step / search box — member lists in V1 are
// small enough that a flat scrollable list is fine.
function TransferOwnershipModal({ spaceId, authToken, onClose, onTransferred }) {
  const [members, setMembers] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [pickedId, setPickedId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${API_BASE}/api/company/members`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(async (r) => {
        if (!r.ok) {
          const e = await r.json().catch(() => ({}));
          throw new Error(e.error || `Members load failed (${r.status})`);
        }
        return r.json();
      })
      .then((body) => {
        if (cancelled) return;
        setMembers(Array.isArray(body.members) ? body.members : []);
      })
      .catch((err) => { if (!cancelled) setError(err.message); });
    return () => { cancelled = true; };
  }, [authToken]);

  const submit = async () => {
    if (!pickedId) return;
    setBusy(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/transfer`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ newOwnerUserId: pickedId }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Transfer failed (${r.status})`);
      }
      onTransferred();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="transfer-modal-title"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--room-card)', borderRadius: 14, width: '100%', maxWidth: 480,
          maxHeight: '85vh', overflow: 'hidden',
          boxShadow: '0 24px 64px rgba(15, 23, 42, 0.22)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          padding: '18px 24px', borderBottom: '1px solid var(--room-border-soft)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <h3 id="transfer-modal-title" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: 'var(--room-text-strong)' }}>
            Transfer Decision Room
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 22, color: 'var(--room-text-subtle)', padding: 4, lineHeight: 1,
            }}
          >×</button>
        </div>
        <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>
          <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--room-text-muted)', lineHeight: 1.5 }}>
            Pick the rep who should own this Space going forward. They become the new
            primary contact; existing share links continue to work. The change is
            logged in your company's audit trail.
          </p>
          {!members && !error && (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--room-text-subtle)' }}>Loading team…</p>
          )}
          {error && (
            <div style={{
              padding: '10px 12px', borderRadius: 8,
              background: 'rgba(239, 68, 68, 0.08)', color: '#dc2626', fontSize: 13,
            }}>
              {error}
            </div>
          )}
          {members && members.length === 0 && (
            <p style={{ margin: 0, fontSize: 13, color: 'var(--room-text-muted)' }}>
              No other team members to transfer to.
            </p>
          )}
          {members && members.length > 0 && (
            <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {members.map((m) => {
                const name = [m.firstName, m.lastName].filter(Boolean).join(' ') || m.email;
                const isPicked = pickedId === m.id;
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => setPickedId(m.id)}
                      style={{
                        width: '100%', padding: '10px 12px', borderRadius: 10,
                        border: `1px solid ${isPicked ? '#a855f7' : 'var(--room-border, #e2e8f0)'}`,
                        background: isPicked ? 'var(--room-accent-soft, #faf5ff)' : 'var(--room-card, #fff)',
                        cursor: 'pointer', textAlign: 'left',
                        display: 'flex', alignItems: 'center', gap: 12,
                        fontFamily: 'inherit',
                      }}
                    >
                      <div style={{
                        width: 32, height: 32, borderRadius: '50%',
                        background: '#ede9fe', color: '#6d28d9',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 12, fontWeight: 700, flex: 'none',
                      }}>
                        {(name || '?').slice(0, 2).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {name}
                          {m.companyRole === 'admin' && (
                            <span style={{ marginLeft: 8, fontSize: 10, color: '#7c3aed', fontWeight: 700, letterSpacing: '0.04em' }}>ADMIN</span>
                          )}
                        </div>
                        <div style={{ fontSize: 12, color: 'var(--room-text-subtle)' }}>
                          {m.email}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
        <div style={{
          padding: '14px 20px', borderTop: '1px solid var(--room-border-soft)',
          display: 'flex', gap: 8, justifyContent: 'flex-end',
        }}>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            style={{
              padding: '8px 16px', borderRadius: 8,
              border: '1px solid var(--room-border)', background: 'var(--room-card)',
              color: 'var(--room-text-body)', cursor: 'pointer',
              fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={!pickedId || busy}
            style={{
              padding: '8px 18px', borderRadius: 8, border: 'none',
              background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
              color: '#fff', cursor: (!pickedId || busy) ? 'not-allowed' : 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              opacity: (!pickedId || busy) ? 0.55 : 1,
            }}
          >
            {busy ? 'Transferring…' : 'Transfer'}
          </button>
        </div>
      </div>
    </div>
  );
}

// Shared styles for the two lifecycle buttons. Pulled out so the two
// buttons can't drift visually as the design evolves.
function lifecycleBtnStyle({ busy, tone }) {
  return {
    flex: 1,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6,
    padding: '7px 10px', borderRadius: 8,
    border: '1px solid #ede9fe',
    background: 'var(--room-accent-soft, #faf5ff)',
    color: tone === 'danger' ? '#b91c1c' : '#6d28d9',
    cursor: busy ? 'wait' : 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
    letterSpacing: '0.01em',
    opacity: busy ? 0.55 : 1,
    transition: 'background 0.12s ease, color 0.12s ease, border-color 0.12s ease',
  };
}

function lifecycleBtnHover(node, tone, on) {
  if (on) {
    if (tone === 'danger') {
      node.style.background = '#fee2e2';
      node.style.borderColor = '#fecaca';
      node.style.color = '#b91c1c';
    } else {
      node.style.background = '#ede9fe';
      node.style.borderColor = '#c4b5fd';
      node.style.color = '#6d28d9';
    }
  } else {
    node.style.background = 'var(--room-accent-soft, #faf5ff)';
    node.style.borderColor = '#ede9fe';
    node.style.color = tone === 'danger' ? '#b91c1c' : '#6d28d9';
  }
}

// Internal share (2026-08-23): colleague-facing link that shows
// internal-only sections and never touches buyer engagement. Owns its
// own panel state so it can render from either placement.
//
// WHY IT MOVED (2026-09-07, flag rooms_internal_share_placement):
// it was a full-width amber button sitting third in the sidebar,
// directly under the two buyer-facing share actions — the loudest
// fill of the three, in prime real estate, for something a seller
// touches rarely. It read as a peer of the buyer send.
//
// The sidebar had been interleaving two audiences. It now groups by
// who the action is for: prospect-facing (Share / Copy link /
// Preview as prospect) up top, internal (Collaborators + this) below.
// The distance from the buyer share also serves the original
// "can't be mistaken for the buyer share" intent better than colour
// alone did — the amber survives as a dot rather than a whole button.
//
// It is deliberately a SIBLING of CollaboratorsSection rather than a
// child: that section returns null while loading, on fetch error, and
// for a non-manager viewing a room with no collaborators, and an
// unrelated fetch must not be able to hide internal sharing.
//
// variant 'legacy'  — the old full-width amber button (flag off).
// variant 'grouped' — compact row under Collaborators (flag on).
function InternalShareButton({ spaceId, authToken, variant }) {
  const [open, setOpen] = useState(false);

  const legacy = variant === 'legacy';

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={legacy ? undefined : 'Share with colleagues — shows internal-only sections, never counts as buyer engagement'}
        style={legacy ? {
          marginTop: 8, width: '100%', padding: '9px 14px',
          borderRadius: 10, border: '1px solid #fcd34d', cursor: 'pointer',
          background: '#fef3c7', color: '#92400e',
          fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
        } : {
          marginTop: 10, display: 'inline-flex', alignItems: 'center', gap: 6,
          background: 'none', border: 'none', padding: 0, cursor: 'pointer',
          fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
          color: 'var(--room-text-body, #334155)',
        }}
      >
        {!legacy && (
          <span
            aria-hidden="true"
            style={{
              width: 6, height: 6, borderRadius: '50%',
              background: '#f59e0b', flexShrink: 0,
            }}
          />
        )}
        {legacy ? 'Share internally · colleagues only' : 'Share internally →'}
      </button>

      {!legacy && (
        <p style={{
          margin: '3px 0 0 12px', fontSize: 11, lineHeight: 1.45,
          color: 'var(--room-text-subtle, #94a3b8)',
        }}>
          Colleagues only · shows internal sections
        </p>
      )}

      {open && (
        <InternalSharePanel
          artifactType="buyer_space"
          artifactId={spaceId}
          authToken={authToken}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

// Share menu — opens a centered modal where the seller picks a
// recipient from the People tab (buyer side), composes the email,
// and hits Send. The modal pattern mirrors the existing follow-up
// email modal so it feels native. Recipients come from
// data.people (buyer side, with email) — NOT the raw distributions
// table, per Daniel's 2026-04-27 feedback. The People tab is the
// canonical "address book" for the Space.
//
// Backend resolves person → distribution: looks up an existing one
// for this email, mints a fresh one if none, so engagement
// attributes correctly even on first send.
function ShareSpaceMenu({ slug, spaceId, authToken, people, prospectCompanyName, sellerName, copyLinkEnabled, internalPlacementMoved }) {
  const [modalOpen, setModalOpen] = useState(false);
  // 'email' | 'link' — which tab the Share modal opens on. The Copy
  // share link button below opens it straight on the link tab.
  const [modalMode, setModalMode] = useState('email');

  // Recipients = buyer-side people with an email. People without
  // emails can't be sent to and aren't selectable. Falls back to
  // disabled state if there are zero eligible people.
  const eligiblePeople = (people || []).filter((p) => p.side === 'buyer' && p.email);

  if (eligiblePeople.length === 0) {
    return (
      <>
      <button
        type="button"
        disabled
        title="Add at least one person with an email on the People tab to send a share link"
        style={{
          marginTop: 12, width: '100%', padding: '10px 14px',
          borderRadius: 10, border: '1px dashed var(--room-border, #cbd5e1)',
          background: 'var(--room-surface)', color: 'var(--room-text-subtle)',
          cursor: 'not-allowed', fontSize: 13, fontFamily: 'inherit', fontWeight: 600,
        }}
      >
        Share Decision Room
      </button>
        {/* The reason was tooltip-only, which keyboards, screen
            readers and the QA agent all missed — the gate read as
            "broken button" instead of a rule (2026-08-27). */}
        <p style={{
          margin: '6px 2px 0', fontSize: 12, lineHeight: 1.5,
          color: 'var(--room-text-muted, #64748b)',
        }}>
          Add at least one buyer-side person with an email on the People tab to enable sharing.
        </p>
      </>
    );
  }

  return (
    <>
      <button
        type="button"
        onClick={() => { setModalMode('email'); setModalOpen(true); }}
        style={{
          marginTop: 12, width: '100%', padding: '10px 14px',
          borderRadius: 10, border: 'none', cursor: 'pointer',
          background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
          color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
          boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
        }}
      >
        Share Decision Room
      </button>

      {/* Copy share link (2026-09-02, flag rooms_copy_share_link): the
          same per-person tracked link the email carries, without the
          email. Exists because corporate mail filters (Defender) hold
          or rewrite the share email and the seller only finds out when
          the prospect says "I never got it" — with the URL in hand
          they paste it into Teams or their own mail client, and the
          prospect's engagement still lands on their record here. */}
      {copyLinkEnabled && (
        <button
          type="button"
          onClick={() => { setModalMode('link'); setModalOpen(true); }}
          style={{
            marginTop: 8, width: '100%', padding: '9px 14px',
            borderRadius: 10, border: '1px solid #c7d2fe', cursor: 'pointer',
            background: 'var(--room-card, #ffffff)', color: '#4f46e5',
            fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
          }}
        >
          Copy share link
        </button>
      )}

      {/* Internal share stays here only while the placement flag is
          off. When it is on it renders under Collaborators instead —
          see InternalShareButton for the reasoning. */}
      {!internalPlacementMoved && (
        <InternalShareButton spaceId={spaceId} authToken={authToken} variant="legacy" />
      )}

      {modalOpen && (
        <ShareSpaceModal
          slug={slug}
          spaceId={spaceId}
          authToken={authToken}
          people={eligiblePeople}
          prospectCompanyName={prospectCompanyName}
          sellerName={sellerName}
          initialMode={modalMode}
          copyLinkEnabled={copyLinkEnabled}
          onClose={() => setModalOpen(false)}
        />
      )}
    </>
  );
}

// Modal for composing + sending the Decision Room link. Pattern matches
// the existing follow-up modal: fixed-position overlay, click backdrop
// to dismiss, ESC closes, body content stops propagation. Body
// textarea has a Revise with AI button next to Send.
//
// Two tabs when copyLinkEnabled (flag rooms_copy_share_link,
// 2026-09-02): "Send by email" (the original flow) and "Copy link",
// which mints the SAME per-person tracked link via
// POST /share-link and copies it, no email. The recipient picker is
// shared — a link is always somebody's link, because that is how the
// prospect's views and replies attribute to them in the engagement
// panel. The email success screen offers the link too, for the case
// the seller worries the message is sitting in a mail filter.
function ShareSpaceModal({ slug, spaceId, authToken, people, prospectCompanyName, sellerName, initialMode, copyLinkEnabled, onClose }) {
  // Default to the first eligible person. Seller picks via dropdown.
  const [personId, setPersonId] = useState(people[0]?.id || '');
  const selectedPerson = people.find((p) => p.id === personId) || people[0];
  const selectedFirstName = (selectedPerson?.fullName || '').split(' ')[0] || 'this person';

  // Link tab state. `link` is the /share-link response for the
  // currently selected person; it resets when the person changes so
  // a stale URL can never be copied under the wrong name.
  const [mode, setMode] = useState(copyLinkEnabled && initialMode === 'link' ? 'link' : 'email');
  const [link, setLink] = useState(null);
  const [linkBusy, setLinkBusy] = useState(false);
  const [linkError, setLinkError] = useState(null);
  const [copied, setCopied] = useState(false);

  const buildDefaultBody = (person) => {
    const firstName = (person?.fullName || '').split(' ')[0] || 'there';
    // No inline link in the default copy — the server-side renderer
    // appends an "Enter the Decision Room" gradient CTA button at the
    // bottom of every email, so the body doesn't need to point at
    // the URL itself. Sellers who want an inline link can still
    // type {{share_link}} anywhere in the message and it'll be
    // substituted with a styled anchor.
    return (
      `Hi ${firstName},\n\n` +
      `I put together a Decision Room for ${prospectCompanyName} with the demo highlights, ` +
      `next steps, and the docs we discussed — everything's in one place below.\n\n` +
      `Feel free to forward to anyone on your team. Reply right inside the Decision Room ` +
      `and it'll come straight back to me.\n\n` +
      `${sellerName || ''}`
    );
  };

  const [subject, setSubject] = useState(`Your Decision Room from ${sellerName || 'TrailerCast'}`);
  const [body, setBody] = useState(buildDefaultBody(selectedPerson));
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sentTo, setSentTo] = useState(null);
  // URL the email carried (share-email returns it) so the success
  // screen can offer it for copying without a second round-trip.
  const [sentUrl, setSentUrl] = useState(null);

  // Re-prefill body when seller picks a different recipient — keeps
  // the salutation in sync with the chosen person. Skips the update
  // if the seller has already typed a custom body (preserves edits).
  useEffect(() => {
    setBody((current) => {
      // Only auto-update when the body still matches some default
      // (a previous person's auto-generated body). If the seller
      // typed something custom, leave it alone.
      const looksAuto = people.some((p) => current === buildDefaultBody(p));
      return looksAuto ? buildDefaultBody(selectedPerson) : current;
    });
    setLink(null);
    setLinkError(null);
    setCopied(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personId]);

  // Clipboard write. Called synchronously from a click so it works in
  // every browser (Safari refuses writes that come after an await);
  // the auto-copy after Create link is best-effort on top of this.
  const copyText = (text) => {
    if (!text || !navigator.clipboard?.writeText) return Promise.resolve(false);
    return navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
      return true;
    }).catch(() => false);
  };

  const createLink = async () => {
    if (!personId) return;
    setLinkBusy(true);
    setLinkError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/share-link`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ personId }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Could not create link (${r.status})`);
      }
      const result = await r.json();
      setLink(result);
      copyText(result.url);
    } catch (err) {
      setLinkError(err.message);
    } finally {
      setLinkBusy(false);
    }
  };

  // ESC closes the modal — standard modal behavior.
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const send = async () => {
    if (!personId || !subject.trim() || !body.trim()) return;
    setSending(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/share-email`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ personId, subject: subject.trim(), message: body.trim() }),
      });
      if (!r.ok) {
        const e = await r.json().catch(() => ({}));
        throw new Error(e.error || `Send failed (${r.status})`);
      }
      const result = await r.json();
      setSentTo(result.sentTo || selectedPerson?.email || '');
      setSentUrl(result.shareUrl || null);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  // Shared URL row: read-only input (select-all on focus for manual
  // copy where the clipboard API is blocked) + a synchronous Copy.
  const renderUrlRow = (url) => (
    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
      <input
        type="text"
        readOnly
        value={url}
        onFocus={(e) => e.target.select()}
        aria-label="Share link"
        style={{
          flex: 1, minWidth: 0, padding: '9px 12px',
          border: '1px solid var(--room-border, #e2e8f0)', borderRadius: 8,
          background: 'var(--room-surface, #f8fafc)', color: 'var(--room-text-body, #334155)',
          fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box',
        }}
      />
      <button
        type="button"
        onClick={() => copyText(url)}
        style={{
          padding: '9px 16px', borderRadius: 8, border: 'none', cursor: 'pointer',
          background: copied ? '#16a34a' : '#4f46e5', color: '#fff',
          fontSize: 13, fontWeight: 600, fontFamily: 'inherit', whiteSpace: 'nowrap',
          transition: 'background 150ms',
        }}
      >
        {copied ? 'Copied ✓' : 'Copy'}
      </button>
    </div>
  );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-space-modal-title"
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--room-card)', borderRadius: 14, width: '100%', maxWidth: 640,
          maxHeight: '90vh', overflow: 'auto',
          boxShadow: '0 24px 64px rgba(15, 23, 42, 0.22)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '18px 24px', borderBottom: '1px solid var(--room-border-soft)',
        }}>
          <h3 id="share-space-modal-title" style={{ margin: 0, fontSize: 18, fontWeight: 700, color: 'var(--room-text-strong)' }}>
            Share Decision Room
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: 22, color: 'var(--room-text-subtle)', padding: 4, lineHeight: 1,
            }}
          >
            ×
          </button>
        </div>

        <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
          {sentTo ? (
            // Success state — replaces the form so the seller knows
            // the send went through. Close button at the bottom.
            <div style={{ textAlign: 'center', padding: '24px 0' }}>
              <div style={{
                width: 56, height: 56, borderRadius: '50%',
                background: '#ecfdf5', color: '#16a34a',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 28, fontWeight: 700, margin: '0 auto 16px',
              }}>
                ✓
              </div>
              <h4 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 700, color: 'var(--room-text-strong)' }}>
                Sent to {sentTo}
              </h4>
              <p style={{ margin: 0, color: 'var(--room-text-muted)', fontSize: 14 }}>
                The recipient will get a branded email with the Decision Room link.
                Replies route back to your inbox.
              </p>
              {copyLinkEnabled && sentUrl && (
                <div style={{ marginTop: 20, textAlign: 'left' }}>
                  <p style={{ margin: 0, fontSize: 13, color: 'var(--room-text-muted)' }}>
                    Worried a mail filter might hold it back? This is the same link — paste it into
                    Teams or your own email and {selectedFirstName}'s views still count here.
                  </p>
                  {renderUrlRow(sentUrl)}
                </div>
              )}
              <button
                type="button"
                onClick={onClose}
                style={{
                  marginTop: 24, padding: '10px 24px', borderRadius: 10,
                  border: 'none', cursor: 'pointer',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
                  color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                }}
              >
                Done
              </button>
            </div>
          ) : (
            <>
              {copyLinkEnabled && (
                <div
                  role="tablist"
                  aria-label="How to share"
                  style={{
                    display: 'inline-flex', gap: 4, padding: 3, borderRadius: 10,
                    background: 'var(--room-surface, #f1f5f9)', alignSelf: 'flex-start',
                  }}
                >
                  {[['email', 'Send by email'], ['link', 'Copy link']].map(([key, label]) => (
                    <button
                      key={key}
                      type="button"
                      role="tab"
                      aria-selected={mode === key}
                      onClick={() => setMode(key)}
                      style={{
                        padding: '6px 14px', borderRadius: 8, border: 'none', cursor: 'pointer',
                        background: mode === key ? 'var(--room-card, #ffffff)' : 'transparent',
                        color: mode === key ? 'var(--room-text-strong, #0f172a)' : 'var(--room-text-muted, #64748b)',
                        boxShadow: mode === key ? '0 1px 3px rgba(15, 23, 42, 0.12)' : 'none',
                        fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
                      }}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              )}

              {/* Recipient picker — buyer-side People only. If the
                  seller's about to send to someone whose email isn't
                  on file yet, they need to add it on the People tab
                  first; that's enforced by the eligiblePeople filter
                  one level up. */}
              <div>
                <label style={{
                  display: 'block', fontSize: 12, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  color: 'var(--room-text-muted)', marginBottom: 6,
                }}>
                  Send to
                </label>
                <select
                  value={personId}
                  onChange={(e) => setPersonId(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px',
                    border: '1px solid var(--room-border)', borderRadius: 8,
                    fontSize: 14, fontFamily: 'inherit',
                    background: 'var(--room-card)', color: 'var(--room-text-strong)',
                    boxSizing: 'border-box',
                  }}
                >
                  {people.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.fullName} — {p.email}{p.role ? ` · ${p.role}` : ''}
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: 12, color: 'var(--room-text-subtle)', margin: '6px 0 0' }}>
                  Recipients come from the People tab. Add someone there first to send to a new stakeholder.
                </p>
              </div>

              {mode === 'link' ? (
                <div>
                  <p style={{ margin: 0, fontSize: 14, lineHeight: 1.55, color: 'var(--room-text-body, #334155)' }}>
                    Get {selectedFirstName}'s personal link to paste into Teams, LinkedIn, a calendar invite,
                    or your own email. It's the same tracked link the email carries, so their views and
                    replies land on their engagement record here — useful when a corporate mail filter
                    holds the email back.
                  </p>
                  {link && link.personId === personId && renderUrlRow(link.url)}
                  {linkError && (
                    <div style={{
                      marginTop: 10, padding: '10px 12px', borderRadius: 8,
                      background: 'rgba(239, 68, 68, 0.08)', color: '#dc2626', fontSize: 13,
                    }}>
                      {linkError}
                    </div>
                  )}
                  <p style={{ fontSize: 12, color: 'var(--room-text-subtle)', margin: '10px 0 0', lineHeight: 1.5 }}>
                    This is {selectedFirstName}'s credential: opening it yourself records a view as them.
                    Use "Preview as prospect" to look at the room.
                  </p>
                </div>
              ) : (
              <>
              <div>
                <label style={{
                  display: 'block', fontSize: 12, fontWeight: 600,
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  color: 'var(--room-text-muted)', marginBottom: 6,
                }}>
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  style={{
                    width: '100%', padding: '10px 12px',
                    border: '1px solid var(--room-border)', borderRadius: 8,
                    background: 'var(--room-card)', color: 'var(--room-text-strong)',
                    fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
                  }}
                />
              </div>

              <div>
                <div style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  marginBottom: 6,
                }}>
                  <label style={{
                    fontSize: 12, fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.05em',
                    color: 'var(--room-text-muted)',
                  }}>
                    Message
                  </label>
                  <ReviseWithAI
                    value={body}
                    setValue={setBody}
                    authToken={authToken}
                    fieldType="welcome"
                    label="Revise with AI"
                  />
                </div>
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  rows={9}
                  style={{
                    width: '100%', padding: '10px 12px',
                    border: '1px solid var(--room-border)', borderRadius: 8,
                    background: 'var(--room-card)', color: 'var(--room-text-strong)',
                    fontSize: 14, fontFamily: 'inherit',
                    resize: 'vertical', boxSizing: 'border-box', lineHeight: 1.5,
                  }}
                />
                <p style={{ fontSize: 12, color: 'var(--room-text-subtle)', margin: '6px 0 0' }}>
                  A branded "Open Decision Room" button is appended automatically. Type <code style={{ background: 'var(--room-card-hover)', padding: '1px 4px', borderRadius: 3 }}>{'{{share_link}}'}</code> anywhere in the body if you also want an inline link.
                </p>
              </div>
              </>
              )}

              {mode === 'email' && error && (
                <div style={{
                  padding: '10px 12px', borderRadius: 8,
                  background: 'rgba(239, 68, 68, 0.08)',
                  color: '#dc2626', fontSize: 13,
                }}>
                  {error}
                </div>
              )}

              <div style={{
                display: 'flex', justifyContent: 'flex-end', gap: 8,
                paddingTop: 8, borderTop: '1px solid var(--room-border-soft)', marginTop: 4,
              }}>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={sending}
                  style={{
                    padding: '10px 18px', borderRadius: 10,
                    border: '1px solid var(--room-border)', background: 'var(--room-card)',
                    color: 'var(--room-text-body)', cursor: 'pointer',
                    fontSize: 14, fontWeight: 500, fontFamily: 'inherit',
                  }}
                >
                  Cancel
                </button>
                {mode === 'link' ? (
                  <button
                    type="button"
                    onClick={() => (link && link.personId === personId ? copyText(link.url) : createLink())}
                    disabled={linkBusy || !personId}
                    style={{
                      padding: '10px 24px', borderRadius: 10,
                      border: 'none', cursor: 'pointer',
                      background: copied
                        ? '#16a34a'
                        : 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
                      color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                      opacity: (linkBusy || !personId) ? 0.5 : 1,
                      boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
                    }}
                  >
                    {linkBusy ? 'Creating…' : copied ? 'Copied ✓' : (link && link.personId === personId) ? 'Copy link' : 'Create link'}
                  </button>
                ) : (
                <button
                  type="button"
                  onClick={send}
                  disabled={sending || !personId || !subject.trim() || !body.trim()}
                  style={{
                    padding: '10px 24px', borderRadius: 10,
                    border: 'none', cursor: 'pointer',
                    background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 50%, #a855f7 100%)',
                    color: '#fff', fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
                    opacity: (sending || !personId || !subject.trim() || !body.trim()) ? 0.5 : 1,
                    boxShadow: '0 4px 12px rgba(99, 102, 241, 0.25)',
                  }}
                >
                  {sending ? 'Sending…' : 'Send'}
                </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// 2026-04-27: legacy share-email components (ShareEmailComposer,
// ProspectPreviewButton, shareActionStyle, previewBtnStyle) deleted —
// see git history. ShareSpaceModal above is the current implementation.

// Demos section: the trailer grid + the "+ Add demo" affordance that
// lets a rep manually curate which trailers the buying committee sees.
// Demos land here via this picker OR the from-call/backfill paths; the
// picker is the only in-product way to add one after the room exists.
function DemosSection({ data, authToken, spaceId, onReload, onOpenDemo }) {
  const [addOpen, setAddOpen] = useState(false);
  const trailers = data.trailers || [];
  const groups = splitRecordings(trailers);

  const addBtn = (
    <button
      type="button"
      onClick={() => setAddOpen(true)}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 8,
        padding: '9px 16px', borderRadius: 9, border: 'none',
        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
        color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer',
        fontFamily: 'inherit', boxShadow: '0 2px 8px rgba(124, 58, 237, 0.25)',
      }}
    >
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <line x1="12" y1="5" x2="12" y2="19" />
        <line x1="5" y1="12" x2="19" y2="12" />
      </svg>
      Add demo
    </button>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <p style={{ margin: 0, color: 'var(--room-text-muted)', fontSize: 14 }}>
          {trailers.length === 0
            ? 'No recordings in this Decision Room yet — add a trailer or a full call to get started.'
            : (<><strong style={{ color: 'var(--room-text-strong)' }}>{countsSummary(trailers)}</strong> in this Decision Room — click any tile to open.</>)}
        </p>
        {addBtn}
      </div>

      {[
        ['Trailers', 'Short, produced cuts of the highlights.', groups.trailers],
        ['Full recordings', 'Complete calls, unedited, for anyone who wants the whole conversation.', groups.fullRecordings],
      ].filter(([, , list]) => list.length > 0).map(([title, blurb, list]) => (
        <div key={title} style={{ marginBottom: 28 }}>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 12 }}>
            <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: 'var(--room-text-strong)' }}>{title}</h3>
            <span style={{ fontSize: 13, color: 'var(--room-text-subtle)' }}>{blurb}</span>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 16,
          }}>
            {list.map((r) => (
              <DemoTile
                key={r.id}
                trailer={r}
                onOpenDemo={onOpenDemo}
                authToken={authToken}
                spaceId={spaceId}
                onReload={onReload}
              />
            ))}
          </div>
        </div>
      ))}

      {addOpen && (
        <AddDemoModal
          authToken={authToken}
          spaceId={spaceId}
          onClose={() => setAddOpen(false)}
          onAdded={() => { setAddOpen(false); onReload && onReload(); }}
        />
      )}
    </div>
  );
}

// Picker modal for attaching existing trailers to the room. Lists the
// rep's completed, company-scoped trailers (not already in this room)
// from GET /attachable-demos, multi-select, then POSTs the selection.
// Handles the 409 "already in another room" case with an explicit
// move-confirm so we never silently steal a trailer from another deal.
function AddDemoModal({ authToken, spaceId, onClose, onAdded }) {
  const [loading, setLoading] = useState(true);
  const [demos, setDemos] = useState([]);
  const [selected, setSelected] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  // When the server says some picks are in another room, we stash the
  // conflict list + show a "Move them here" confirm instead of erroring.
  const [moveConfirm, setMoveConfirm] = useState(null); // null | { conflicts: [...] }

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !submitting) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [submitting, onClose]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/attachable-demos`, {
      headers: { Authorization: `Bearer ${authToken}` },
    })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.error || `Failed to load (${r.status})`);
        return body;
      })
      .then((body) => { if (!cancelled) { setDemos(Array.isArray(body.demos) ? body.demos : []); setLoading(false); } })
      .catch((err) => { if (!cancelled) { setError(err.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [authToken, spaceId]);

  const toggle = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async (move = false) => {
    if (selected.size === 0) { setError('Pick at least one trailer to add.'); return; }
    setSubmitting(true);
    setError(null);
    try {
      const resp = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/demos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ demoIds: Array.from(selected), move }),
      });
      const body = await resp.json().catch(() => ({}));
      if (resp.status === 409 && body.needsMoveConfirm) {
        // Surface the conflict + offer the move. Stay open.
        setMoveConfirm({ conflicts: body.conflicts || [] });
        setSubmitting(false);
        return;
      }
      if (!resp.ok) throw new Error(body.error || `Add failed (${resp.status})`);
      onAdded();
    } catch (err) {
      setError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <div
      onClick={() => { if (!submitting) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(15, 23, 42, 0.55)', backdropFilter: 'blur(2px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-label="Add a trailer to this Decision Room"
        style={{
          background: 'var(--room-card)', borderRadius: 16, width: '100%', maxWidth: 560,
          maxHeight: '80vh', display: 'flex', flexDirection: 'column',
          border: '1px solid var(--room-border)', boxShadow: '0 24px 60px rgba(15, 23, 42, 0.3)',
        }}
      >
        <div style={{ padding: '24px 28px 12px' }}>
          <h2 style={{ margin: '0 0 4px', fontSize: 20, fontWeight: 700, color: 'var(--room-text-strong)' }}>
            Add a demo
          </h2>
          <p style={{ margin: 0, fontSize: 13, color: 'var(--room-text-muted)' }}>
            Pick which of your completed trailers to show in this room.
          </p>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: '4px 28px' }}>
          {loading && <p style={{ color: 'var(--room-text-muted)', fontSize: 14 }}>Loading your trailers…</p>}
          {!loading && error && !moveConfirm && (
            <div style={{ fontSize: 13, color: '#dc2626', padding: '8px 0' }}>{error}</div>
          )}
          {!loading && !error && demos.length === 0 && (
            <p style={{ color: 'var(--room-text-muted)', fontSize: 14 }}>
              No eligible trailers — every completed trailer you have is already in this
              room, or you haven't created any yet.
            </p>
          )}
          {!loading && demos.map((d) => {
            const isSel = selected.has(d.id);
            return (
              <label
                key={d.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
                  borderRadius: 10, cursor: 'pointer', marginBottom: 6,
                  border: `1px solid ${isSel ? '#a855f7' : 'var(--room-border)'}`,
                  background: isSel ? 'rgba(168, 85, 247, 0.06)' : 'transparent',
                }}
              >
                <input type="checkbox" checked={isSel} onChange={() => toggle(d.id)} style={{ width: 16, height: 16, flex: 'none' }} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.prospectName || d.filename || 'Untitled trailer'}
                  </div>
                  {/* rooms_any_recording: when the picker includes
                      meetings/qualifying calls, say what each row IS.
                      Attaching a meeting shares the FULL recording, not
                      a curated cut — the seller must know that before
                      clicking, not after the prospect has watched their
                      internal debrief. Demo rows stay unbadged: the old
                      picker's look is the flag-off look. */}
                  {d.recordingKind && !['demo', 'browser_recording', 'uploaded_video'].includes(d.recordingKind) && (
                    <div style={{ fontSize: 12, color: 'var(--room-text-muted, #64748b)', marginTop: 2 }}>
                      {d.recordingKind === 'meeting' ? 'Meeting' : 'Qualifying call'}
                      {' · full recording shared'}
                      {d.durationSeconds ? ` · ${Math.round(d.durationSeconds / 60)} min` : ''}
                    </div>
                  )}
                  {d.inAnotherRoom && (
                    <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
                      Currently in “{d.currentSpaceName || 'another room'}” — adding here will move it.
                    </div>
                  )}
                </div>
              </label>
            );
          })}
        </div>

        {moveConfirm && (
          <div style={{ margin: '0 28px 12px', padding: '12px 14px', borderRadius: 10, background: '#fffbeb', border: '1px solid #fde68a' }}>
            <div style={{ fontSize: 13, color: '#92400e', fontWeight: 600, marginBottom: 4 }}>
              {moveConfirm.conflicts.length} selected trailer{moveConfirm.conflicts.length === 1 ? ' is' : 's are'} already in another Decision Room.
            </div>
            <div style={{ fontSize: 13, color: '#92400e' }}>
              Moving will remove {moveConfirm.conflicts.length === 1 ? 'it' : 'them'} from the other room. Continue?
            </div>
          </div>
        )}

        <div style={{ padding: '12px 28px 24px', borderTop: '1px solid var(--room-border-soft)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <span style={{ fontSize: 13, color: 'var(--room-text-muted)' }}>
            {selected.size} selected
          </span>
          <div style={{ display: 'flex', gap: 10 }}>
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              style={{
                padding: '10px 16px', borderRadius: 8, border: '1px solid var(--room-border)',
                background: 'var(--room-card)', color: 'var(--room-text-strong)',
                fontSize: 14, fontWeight: 500, cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: 'inherit',
              }}
            >
              Cancel
            </button>
            {moveConfirm ? (
              <button
                type="button"
                onClick={() => submit(true)}
                disabled={submitting}
                style={{
                  padding: '10px 18px', borderRadius: 8, border: 'none',
                  background: '#d97706', color: '#fff', fontSize: 14, fontWeight: 600,
                  cursor: submitting ? 'wait' : 'pointer', fontFamily: 'inherit', opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? 'Moving…' : 'Move them here'}
              </button>
            ) : (
              <button
                type="button"
                onClick={() => submit(false)}
                disabled={submitting || selected.size === 0}
                style={{
                  padding: '10px 18px', borderRadius: 8, border: 'none',
                  background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                  color: '#fff', fontSize: 14, fontWeight: 600,
                  cursor: (submitting || selected.size === 0) ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit', opacity: (submitting || selected.size === 0) ? 0.6 : 1,
                }}
              >
                {submitting ? 'Adding…' : `Add ${selected.size || ''}`.trim()}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// One demo tile in the Demos section. Wraps the previous inline JSX
// so we can hang a kebab "Remove from Decision Room" action on each
// trailer without bloating SectionContent. Detach is local-only — the
// trailer itself + its share links survive (FK ON DELETE SET NULL); we
// just clear demos.buyer_space_id so it stops showing here.
// Poster for a full recording that has no sprite: ReelThumb's fallback
// streams the trailer file, which a raw call doesn't have.
function FullRecordingPoster() {
  return (
    <div style={{
      position: 'absolute', inset: 0,
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
      background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', color: '#cbd5e1',
    }}>
      <svg width="34" height="34" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="5" width="14" height="14" rx="2" />
        <path d="M17 10l4-2v8l-4-2z" />
      </svg>
      <span style={{ fontSize: 12, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase' }}>Full recording</span>
    </div>
  );
}

function DemoTile({ trailer, onOpenDemo, authToken, spaceId, onReload }) {
  const r = trailer;
  const full = isFullRecording(r);
  const [menuOpen, setMenuOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const isCompleted = r.status === 'completed';
  const handleClick = isCompleted && onOpenDemo
    ? () => onOpenDemo(r.id)
    : null;

  const detach = async () => {
    if (!window.confirm(
      `Remove "${r.prospectName || r.filename}" from this Decision Room?\n\n` +
      `The recording and its share links survive — they just go back to "ungrouped" ` +
      `in your library. You can re-attach it to another Space later.`
    )) return;
    setBusy(true);
    try {
      const resp = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/demos/${r.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!resp.ok) {
        const e = await resp.json().catch(() => ({}));
        throw new Error(e.error || `Remove failed (${resp.status})`);
      }
      onReload && onReload();
    } catch (err) {
      alert(err.message);
    } finally {
      setBusy(false);
      setMenuOpen(false);
    }
  };

  // Close the menu on outside click. Cheap effect — only attaches
  // the handler while the menu is open.
  useEffect(() => {
    if (!menuOpen) return;
    const onDocClick = () => setMenuOpen(false);
    window.addEventListener('click', onDocClick);
    return () => window.removeEventListener('click', onDocClick);
  }, [menuOpen]);

  return (
    <div
      onClick={handleClick}
      style={{
        background: 'var(--room-card)', borderRadius: 12, overflow: 'hidden',
        border: '1px solid var(--room-border)',
        cursor: handleClick ? 'pointer' : 'default',
        opacity: handleClick ? 1 : 0.6,
        transition: 'transform 0.12s ease, box-shadow 0.12s ease, border-color 0.12s ease',
        display: 'flex', flexDirection: 'column', position: 'relative',
      }}
      onMouseEnter={(e) => {
        if (handleClick) {
          e.currentTarget.style.borderColor = '#a855f7';
          e.currentTarget.style.boxShadow = '0 4px 16px rgba(168, 85, 247, 0.12)';
          e.currentTarget.style.transform = 'translateY(-2px)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.borderColor = 'var(--room-border, #e2e8f0)';
        e.currentTarget.style.boxShadow = 'none';
        e.currentTarget.style.transform = 'translateY(0)';
      }}
      title={isCompleted ? `Open this ${kindLabel(r).toLowerCase()}` : `${kindLabel(r)} is ${r.status} — not yet ready`}
    >
      <div style={{
        position: 'relative',
        width: '100%', aspectRatio: '16 / 9',
        background: 'var(--room-text-strong, #0f172a)', overflow: 'hidden',
      }}>
        {isCompleted && full && !r.hasThumbnailSprite ? (
          <FullRecordingPoster />
        ) : isCompleted ? (
          <ReelThumb
            demoId={r.id}
            hasSprite={r.hasThumbnailSprite}
            className="reel-thumb-detail"
          />
        ) : (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: 'var(--room-text-subtle)', fontSize: 13,
          }}>
            {r.status === 'failed' ? 'Failed to process' : `${r.status}…`}
          </div>
        )}
        {isCompleted && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'linear-gradient(180deg, transparent 60%, rgba(0, 0, 0, 0.55) 100%)',
            pointerEvents: 'none',
          }} />
        )}
        <span style={{
          position: 'absolute', left: 10, bottom: 10, zIndex: 2,
          padding: '4px 9px', borderRadius: 999,
          fontSize: 11, fontWeight: 700, letterSpacing: '0.03em', textTransform: 'uppercase',
          color: '#fff',
          background: full ? 'rgba(15, 23, 42, 0.78)' : 'linear-gradient(135deg, #6366f1 0%, #a855f7 100%)',
          backdropFilter: 'blur(4px)', pointerEvents: 'none',
        }}>
          {kindLabel(r)}
        </span>
      </div>

      {/* Kebab — overlays the top-right corner of the thumbnail.
          Sits above the gradient so it's legible regardless of
          thumbnail content. stopPropagation on every click so it
          never bubbles up to the tile's open-reel handler. */}
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); setMenuOpen((v) => !v); }}
        title="Trailer options"
        aria-label="Trailer options"
        style={{
          position: 'absolute', top: 8, right: 8, zIndex: 2,
          width: 28, height: 28, borderRadius: 8, border: 'none',
          background: 'rgba(15, 23, 42, 0.55)', color: '#fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', backdropFilter: 'blur(4px)',
          transition: 'background 0.12s ease',
        }}
        onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(15, 23, 42, 0.78)'; }}
        onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(15, 23, 42, 0.55)'; }}
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
          <circle cx="12" cy="5" r="1.6" />
          <circle cx="12" cy="12" r="1.6" />
          <circle cx="12" cy="19" r="1.6" />
        </svg>
      </button>

      {menuOpen && (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            position: 'absolute', top: 40, right: 8, zIndex: 3,
            background: 'var(--room-card)', border: '1px solid var(--room-border)',
            borderRadius: 10, boxShadow: '0 12px 24px rgba(15, 23, 42, 0.14)',
            minWidth: 200, padding: 4,
          }}
        >
          <button
            type="button"
            onClick={detach}
            disabled={busy}
            style={{
              width: '100%', textAlign: 'left',
              padding: '10px 12px', borderRadius: 6,
              background: 'none', border: 'none',
              color: '#b91c1c', cursor: busy ? 'wait' : 'pointer',
              fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
              display: 'flex', alignItems: 'center', gap: 8,
            }}
            onMouseEnter={(e) => { if (!busy) e.currentTarget.style.background = '#fee2e2'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'none'; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flex: 'none' }}>
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
            {busy ? 'Removing…' : 'Remove from Decision Room'}
          </button>
        </div>
      )}

      <div style={{ padding: '12px 14px' }}>
        <div style={{
          fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)',
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {r.prospectName || r.filename}
        </div>
        <div style={{ fontSize: 12, color: 'var(--room-text-subtle)', marginTop: 2 }}>
          {kindBlurb(r)} · {fmtDate(r.completedAt || r.createdAt)}
        </div>
      </div>
    </div>
  );
}

function Placeholder({ children }) {
  return (
    <div style={{ background: 'var(--room-card)', border: '1px solid var(--room-border)', borderRadius: 12, padding: 24 }}>
      {children}
    </div>
  );
}

const preStyle = {
  background: 'var(--room-surface)', border: '1px solid var(--room-border)', borderRadius: 8,
  padding: '12px 16px', fontSize: 13, color: 'var(--room-text-body)',
  whiteSpace: 'pre-wrap', margin: '8px 0 0',
};

const listItemStyle = {
  display: 'flex', alignItems: 'center', gap: 10,
  padding: '10px 12px', borderBottom: '1px solid var(--room-border-soft)',
};

// ─── Handoff Brief panel ──────────────────────────────────────────
//
// Internal-only artifact. Owner / admin / collaborator can generate
// or regenerate. Brief is persisted server-side (buyer_spaces.handoff_brief)
// so re-opens are instant — only "Generate" or "Regenerate" hits Claude.
//
// Layout: timestamp + by-whom strip, generate/regenerate button,
// then the structured sections (headline, customer profile, deal
// summary, stakeholders, why-they-bought, success criteria,
// promises, risk flags, implementation expectations, key recordings,
// next actions, signature summary).
function HandoffBriefPanel({ data, authToken, spaceId }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [brief, setBrief] = useState(null);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [generatedBy, setGeneratedBy] = useState(null);
  const [generating, setGenerating] = useState(false);

  // Load existing brief on mount.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/handoff-brief`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        if (!r.ok) throw new Error(`Fetch failed (${r.status})`);
        const body = await r.json();
        if (cancelled) return;
        setBrief(body.brief || null);
        setGeneratedAt(body.generatedAt || null);
        setGeneratedBy(body.generatedBy || null);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [spaceId, authToken]);

  const generate = async () => {
    setGenerating(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/handoff-brief/generate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const body = await r.json();
      if (!r.ok) throw new Error(body.error || `Generate failed (${r.status})`);
      setBrief(body.brief);
      setGeneratedAt(body.generatedAt);
      setGeneratedBy(body.generatedBy);
    } catch (err) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  // Fetch the PDF as a blob so the bearer token rides in the
  // Authorization header (a plain <a href> can't carry it). Then
  // trigger a download via an in-memory blob URL. Filename comes
  // from the server's Content-Disposition; if the server didn't
  // set one, fall back to a date-stamped default.
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const downloadPdf = async () => {
    setDownloadingPdf(true);
    setError(null);
    try {
      const r = await fetch(`${API_BASE}/api/buyer-spaces/${spaceId}/handoff-brief.pdf`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw new Error(errBody.error || `PDF download failed (${r.status})`);
      }
      const blob = await r.blob();
      const cd = r.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const filename = m ? m[1] : `handoff-brief-${new Date().toISOString().slice(0, 10)}.pdf`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      // Revoke after a short delay so the click handler has time to
      // initiate the download — Safari especially can choke if the
      // URL is revoked synchronously.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloadingPdf(false);
    }
  };

  if (loading) {
    return <Placeholder><p style={{ margin: 0, color: 'var(--room-text-muted)' }}>Loading brief…</p></Placeholder>;
  }

  // Empty state — no brief yet.
  if (!brief) {
    return (
      <div>
        <Placeholder>
          <h3 style={{ margin: '0 0 8px', fontSize: 16, fontWeight: 600, color: 'var(--room-text-strong)' }}>
            Sales Handoff Brief
          </h3>
          <p style={{ margin: '0 0 16px', color: 'var(--room-text-muted)', fontSize: 14, lineHeight: 1.55 }}>
            One-page artifact for the implementation team. Distills every linked recording, the People ledger,
            ROI inputs, and your MAP commitments into a structured brief the post-sales lead can read in 90 seconds.
            Generate when the deal closes (or whenever you want to share context with a teammate joining the account).
          </p>
          <button
            type="button"
            onClick={generate}
            disabled={generating}
            style={generateBtnStyle(generating)}
          >
            {generating ? 'Generating brief…' : 'Generate brief'}
          </button>
          {error && <div style={errorStyle}>{error}</div>}
        </Placeholder>
      </div>
    );
  }

  // Filled state — render the structured brief.
  const cp = brief.customer_profile || {};
  const ds = brief.deal_summary || {};
  const sh = brief.stakeholders || {};
  const ie = brief.implementation_expectations || {};

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* Header strip: meta + download PDF + regenerate */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 12, flexWrap: 'wrap',
        padding: '10px 14px', background: 'var(--room-surface)',
        border: '1px solid var(--room-border-soft)', borderRadius: 10,
      }}>
        <div style={{ fontSize: 12, color: 'var(--room-text-muted)' }}>
          Generated {generatedAt ? fmtDate(generatedAt) : 'just now'}
          {generatedBy?.name ? ` by ${generatedBy.name}` : ''}
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            type="button"
            onClick={downloadPdf}
            disabled={downloadingPdf || generating}
            style={regenerateBtnStyle(downloadingPdf)}
            title="Download as PDF — for emailing the implementation team or attaching to a CRM record"
          >
            {downloadingPdf ? 'Preparing PDF…' : 'Download PDF'}
          </button>
          <button
            type="button"
            onClick={generate}
            disabled={generating || downloadingPdf}
            style={regenerateBtnStyle(generating)}
          >
            {generating ? 'Regenerating…' : 'Regenerate'}
          </button>
        </div>
      </div>

      {error && <div style={errorStyle}>{error}</div>}

      {/* Headline */}
      {brief.headline && (
        <BriefCard>
          <p style={{ margin: 0, fontSize: 18, lineHeight: 1.45, color: 'var(--room-text-strong)', fontWeight: 600 }}>
            {brief.headline}
          </p>
        </BriefCard>
      )}

      {/* Customer profile + deal summary side-by-side on wide screens */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 }}>
        <BriefCard title="Customer profile">
          <BriefField label="Company"        value={cp.company_name} />
          <BriefField label="Industry"       value={cp.industry} />
          <BriefField label="Primary use"    value={cp.primary_use_case} />
          <BriefField label="Team size"      value={cp.team_size_estimate} />
        </BriefCard>
        <BriefCard title="Deal summary">
          <BriefField label="Tier"           value={ds.tier} />
          <BriefField label="Seats"          value={ds.seats || null} />
          <BriefField label="Estimated ARR"  value={ds.estimated_arr_usd ? `$${Number(ds.estimated_arr_usd).toLocaleString()}` : null} />
          <BriefField label="Won"            value={ds.won_at} />
          <BriefField label="Term (months)"  value={ds.contract_term_months || null} />
        </BriefCard>
      </div>

      {/* Stakeholders */}
      <BriefCard title="Stakeholders">
        {sh.champion?.name && <StakeholderRow role="Champion" person={sh.champion} />}
        {sh.economic_buyer?.name && <StakeholderRow role="Economic buyer" person={sh.economic_buyer} />}
        {Array.isArray(sh.evaluation_committee) && sh.evaluation_committee.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={briefLabelStyle}>Evaluation committee</div>
            {sh.evaluation_committee.map((p, i) => (
              <div key={i} style={{ fontSize: 13, color: 'var(--room-text-body)' }}>
                {p.name}{p.title ? ` (${p.title})` : ''}{p.role ? ` — ${p.role}` : ''}
              </div>
            ))}
          </div>
        )}
        {Array.isArray(sh.your_team) && sh.your_team.length > 0 && (
          <div style={{ marginTop: 8 }}>
            <div style={briefLabelStyle}>Our team</div>
            {sh.your_team.map((p, i) => (
              <div key={i} style={{ fontSize: 13, color: 'var(--room-text-body)' }}>
                {p.name}{p.role ? ` — ${p.role}` : ''}
              </div>
            ))}
          </div>
        )}
      </BriefCard>

      <BriefList title="Why they bought"     items={brief.why_they_bought} />
      <BriefList title="Success criteria"    items={brief.success_criteria} />
      <BriefList title="Promises made"       items={brief.promises_made} />
      <BriefList title="Risk flags"          items={brief.risk_flags} tone="warn" />

      {/* Implementation expectations */}
      {(ie.go_live_target || (ie.key_dates && ie.key_dates.length) || (ie.must_be_ready_by_kickoff && ie.must_be_ready_by_kickoff.length)) && (
        <BriefCard title="Implementation expectations">
          <BriefField label="Go-live target" value={ie.go_live_target} />
          {Array.isArray(ie.key_dates) && ie.key_dates.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={briefLabelStyle}>Key dates</div>
              <ul style={briefUlStyle}>{ie.key_dates.map((d, i) => <li key={i}>{d}</li>)}</ul>
            </div>
          )}
          {Array.isArray(ie.must_be_ready_by_kickoff) && ie.must_be_ready_by_kickoff.length > 0 && (
            <div style={{ marginTop: 8 }}>
              <div style={briefLabelStyle}>Must be ready by kickoff</div>
              <ul style={briefUlStyle}>{ie.must_be_ready_by_kickoff.map((d, i) => <li key={i}>{d}</li>)}</ul>
            </div>
          )}
        </BriefCard>
      )}

      {/* Key recordings */}
      {Array.isArray(brief.key_recordings) && brief.key_recordings.length > 0 && (
        <BriefCard title="Key recordings">
          {brief.key_recordings.map((r, i) => (
            <div key={i} style={{ padding: '8px 0', borderBottom: i === brief.key_recordings.length - 1 ? 'none' : '1px solid var(--room-border-soft)' }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--room-text-strong)' }}>
                {r.kind || 'recording'} · {r.date || ''}
              </div>
              <div style={{ fontSize: 13, color: 'var(--room-text-body)', marginTop: 2 }}>
                {r.headline}
              </div>
            </div>
          ))}
        </BriefCard>
      )}

      <BriefList title="Next actions (week one)" items={brief.next_actions} />

      {/* Signature summary — the qualitative paragraph */}
      {brief.signature_summary && (
        <BriefCard title="Implementation lead's mental model">
          <p style={{ margin: 0, fontSize: 14, lineHeight: 1.6, color: 'var(--room-text-body)' }}>
            {brief.signature_summary}
          </p>
        </BriefCard>
      )}
    </div>
  );
}

function BriefCard({ title, children }) {
  return (
    <div style={{ background: 'var(--room-card)', border: '1px solid var(--room-border)', borderRadius: 12, padding: 16 }}>
      {title && <h3 style={{ margin: '0 0 10px', fontSize: 14, fontWeight: 600, color: 'var(--room-text-strong)', textTransform: 'uppercase', letterSpacing: 0.4 }}>{title}</h3>}
      {children}
    </div>
  );
}

function BriefField({ label, value }) {
  if (value === null || value === undefined || value === '') return null;
  return (
    <div style={{ display: 'flex', gap: 10, padding: '4px 0', fontSize: 13 }}>
      <span style={{ minWidth: 110, color: 'var(--room-text-muted)' }}>{label}</span>
      <span style={{ color: 'var(--room-text-body)', flex: 1 }}>{String(value)}</span>
    </div>
  );
}

function BriefList({ title, items, tone }) {
  if (!Array.isArray(items) || items.length === 0) return null;
  const isWarn = tone === 'warn';
  return (
    <div style={{
      background: isWarn ? '#fef3c7' : 'var(--room-card)',
      border: `1px solid ${isWarn ? '#fcd34d' : 'var(--room-border)'}`,
      borderRadius: 12, padding: 16,
    }}>
      <h3 style={{
        margin: '0 0 10px', fontSize: 14, fontWeight: 600,
        color: isWarn ? '#92400e' : 'var(--room-text-strong)',
        textTransform: 'uppercase', letterSpacing: 0.4,
      }}>{title}</h3>
      <ul style={briefUlStyle}>
        {items.map((it, i) => <li key={i} style={{ marginBottom: 6 }}>{typeof it === 'string' ? it : JSON.stringify(it)}</li>)}
      </ul>
    </div>
  );
}

function StakeholderRow({ role, person }) {
  return (
    <div style={{ padding: '6px 0' }}>
      <div style={briefLabelStyle}>{role}</div>
      <div style={{ fontSize: 13, color: 'var(--room-text-body)' }}>
        {person.name}{person.title ? ` (${person.title})` : ''}{person.email ? ` · ${person.email}` : ''}
      </div>
    </div>
  );
}

const briefLabelStyle = {
  fontSize: 11, color: 'var(--room-text-muted)', textTransform: 'uppercase',
  letterSpacing: 0.4, fontWeight: 600, marginBottom: 2,
};

const briefUlStyle = {
  margin: 0, paddingLeft: 18, fontSize: 13,
  color: 'var(--room-text-body)', lineHeight: 1.55,
};

const errorStyle = {
  marginTop: 12, padding: '8px 12px', borderRadius: 8,
  background: '#fee2e2', border: '1px solid #fecaca',
  color: '#991b1b', fontSize: 13,
};

function generateBtnStyle(busy) {
  return {
    padding: '10px 18px', borderRadius: 10,
    border: 'none', background: '#7c3aed', color: '#fff',
    cursor: busy ? 'wait' : 'pointer',
    fontSize: 14, fontWeight: 600, fontFamily: 'inherit',
    opacity: busy ? 0.7 : 1,
  };
}

function regenerateBtnStyle(busy) {
  return {
    padding: '6px 12px', borderRadius: 8,
    border: '1px solid #c4b5fd',
    background: 'var(--room-accent-soft, #faf5ff)', color: '#7c3aed',
    cursor: busy ? 'wait' : 'pointer',
    fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
    opacity: busy ? 0.7 : 1,
  };
}
